import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Select } from "antd";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { useEffect, useMemo, useRef, useState } from "react";
import { getAllStudentByYear, getStudentById } from "../../../api/etudiants";
import { getStudentPaiementPlans } from "../../../api/studentPaiementPlan";
import { createDiscount } from "../../../api/bourse_remise";

type Props = {
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
  initialStudentId?: string;
  lockStudent?: boolean;
  currency?: string;
};

type StudentPaymentPlan = {
  id: string;
  remaining?: number;
  paid?: number;
  discount?: number;
  PaymentPlan?: {
    amount?: number;
    due_date?: string;
    label?: string;
  };
};

enum DiscountType {
  BOURSE = "Bourse",
  REMISE = "Reduction",
}

const CreateBourseRemiseModal = ({
  show,
  onClose,
  onSaved,
  initialStudentId,
  lockStudent = false,
  currency = "FCFA",
}: Props) => {
  const getModalContainer = () => {
    const modalElement = document.getElementById("create_bourse_remise_modal");
    return modalElement ? modalElement : document.body;
  };

  //Initialisation de l'année academmique actuelle
  const { selectedYear } = useAcademicYear();

  //State pour stocker une liste d'étudiants formatés pour le select
  const [students, setStudents] = useState<
    {
      id: string;
      lastname: string;
      firstname: string;
      levelAbbr?: string;
      classAbbr?: string;
    }[]
  >([]);

  const plansRequest = useRef(0);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [paiementPlans, setPaiementPlans] = useState<any[]>([]);
  const [studentParents, setStudentParents] = useState<any[]>([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [discountMode, setDiscountMode] = useState<"AMOUNT" | "PERCENTAGE">(
    "PERCENTAGE",
  );
  const [percentage, setPercentage] = useState<number>(100);

  // schéma de validation de notre formulaire
  const createSchema = z.object({
    description: z.string().optional(),
    correspondant: z.string().optional(),
    number_corresponding: z.string().optional(),
    type: z.enum([DiscountType.BOURSE, DiscountType.REMISE]),
    id_studentProfil: z.string().nonempty("Veuillez sélectionner un étudiant"),
    ids_paiement_plan: z
      .array(z.string())
      .min(1, "Veuillez sélectionner au moins un plan de paiement"),
    amount: z.number().positive("Le montant doit être supérieur à zéro"),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      type: DiscountType.BOURSE,
      description: "",
      correspondant: "",
      number_corresponding: "",
      amount: 0,
      ids_paiement_plan: [],
    },
  });

  // fonction pour récupérer tous les étudiants de l'année academique
  const fetchStudentsForYear = async () => {
    try {
      if (!selectedYear?.id) return [];
      const response = await getAllStudentByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.BOURSE_REMISE, Action.CREATE),
      );
      const raw = response.data || [];
      // console.log("raw", raw);
      const formatted = (raw || [])
        .map((s: any) => {
          const id = String(s?.StudentProfil?.id ?? s?.id ?? "");
          const firstname = s?.StudentProfil?.firstname ?? s?.firstname ?? "";
          const lastname = s?.StudentProfil?.lastname ?? s?.lastname ?? "";
          const levelAbbr =
            s?.Registrations?.[0]?.Class?.Level?.abbreviation ??
            s?.Level?.abbreviation ??
            "";
          const classAbbr =
            s?.Registrations?.[0]?.Class?.abbreviation ??
            s?.Class?.abbreviation ??
            "";
          return { id, firstname, lastname, levelAbbr, classAbbr };
        })
        .sort((a: any, b: any) => {
          // First by level abbreviation, then by class abbreviation
          const levelCmp = a.levelAbbr.localeCompare(b.levelAbbr);
          return levelCmp !== 0
            ? levelCmp
            : a.classAbbr.localeCompare(b.classAbbr);
        });
      setStudents(formatted);
      setValue("id_studentProfil", initialStudentId || formatted[0]?.id || "");
      // fetchStudentPlanForStudentSelected();
    } catch (error: any) {
      // console.log("Erreur lors du cargement des étudiants: ", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors du chargement des étudiants",
      );
    }
  };

  const fetchStudentPlanForStudentSelected = async () => {
    const request = ++plansRequest.current;
    setPaiementPlans([]);
    setLoadingPlans(true);
    try {
      const response = await getStudentPaiementPlans(
        watch("id_studentProfil"),
        selectedYear?.id,
        buildPermissionContext(Resource.BOURSE_REMISE, Action.CREATE),
      );
      // console.log(
      //   "Plans de paiement de l'étudiant:",
      //   response.data?.map((p: any) => p) || [],
      // );
      if (request === plansRequest.current) setPaiementPlans(response.data || []);
    } catch (error: any) {
      // console.log(
      //   "Erreur lors du chargement des plans de paiement de l'étudiant: ",
      //   error,
      // );
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erreur lors du chargement des plans de paiement de l'étudiant",
      );
    } finally {
      if (request === plansRequest.current) setLoadingPlans(false);
    }
  };

  //Traitement après validation du formulaire de création d'un niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      if (loadingPlans || data.ids_paiement_plan.some(id => {
        const plan = paiementPlans.find(p => p.id === id);
        return !plan || getRemainingAmount(plan) <= 0 || (planAllocations[id] || 0) > getRemainingAmount(plan);
      })) {
        toast.error("Un plan est soldé ou sa répartition dépasse le reste à payer. Rechargez les plans.");
        return;
      }
      if (Math.abs(totalAllocated - (data.amount || 0)) > 0.01) {
        toast.error(
          "La somme des montants par plan doit correspondre au montant de la bourse/remise",
        );
        return;
      }

      const payload = {
        id_studentProfil: data.id_studentProfil,
        amount: data.amount,
        type: data.type,
        description: data.description,
        correspondant: data.correspondant,
        selectedPlans: (data.ids_paiement_plan || []).map((pid: string) => ({
          id: pid,
          amount: planAllocations[pid] ?? 0,
        })),
        corresponding: data.correspondant,
        number_corresponding: data.number_corresponding,
      };
      // console.log("payload", payload);
      //Envoi des datas pour création vers le backend
      await createDiscount(payload);
      if (onSaved) onSaved(); // On fait un reload de la liste
      reset(); //On réinitialise les champs du formulaire

      toast.success("Bourse / Rémise ajouté !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // console.log("Erreur lors de la création de la bourse / remise:", error);
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const parseDate = (d?: string) => {
    if (!d) return new Date();
    const parts = d.split(".");
    if (parts.length !== 3) return new Date();
    return new Date(
      parseInt(parts[2], 10),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[0], 10),
    );
  };

  const getRemainingAmount = (plan: StudentPaymentPlan) => {
    if (plan.remaining !== undefined) return Math.max(0, Number(plan.remaining));
    const amount = Number(plan.PaymentPlan?.amount || 0);
    const paid = Number(plan.paid || 0);
    const discount = Number(plan.discount || 0);
    return Math.max(0, amount - paid - discount);
  };

  const autoDistribute = (
    totalAmount: number,
    selectedPlans: StudentPaymentPlan[],
  ) => {
    let remaining = totalAmount;

    const sortedPlans = [...selectedPlans].sort(
      (a, b) =>
        parseDate(a.PaymentPlan?.due_date).getTime() -
        parseDate(b.PaymentPlan?.due_date).getTime(),
    );

    const allocations: Record<string, number> = {};

    for (const plan of sortedPlans) {
      const remainingPlan = getRemainingAmount(plan);

      const allocated = Math.min(remaining, remainingPlan);

      allocations[plan.id] = allocated;

      remaining -= allocated;

      if (remaining <= 0) break;
    }

    return allocations;
  };

  const handleAutoDistribute = () => {
    const plans = paiementPlans.filter((plan) =>
      id_paymentPlans.includes(plan.id),
    );

    setPlanAllocations(autoDistribute(totalAmount, plans));
  };

  useEffect(() => {
    if (!show) return;
    fetchStudentsForYear();
  }, [show, selectedYear?.id, initialStudentId]);

  const selectedStudent = students.find(
    (s: any) => s.id === watch("id_studentProfil"),
  );
  const id_paymentPlans = watch("ids_paiement_plan") || [];
  const [planAllocations, setPlanAllocations] = useState<
    Record<string, number>
  >({});
  const totalAmount = watch("amount") || 0;

  const selectedPlans = paiementPlans.filter((plan) =>
    id_paymentPlans.includes(plan.id),
  );

  const selectedPlansRemaining = useMemo(
    () =>
      selectedPlans.reduce(
        (acc, plan) => acc + getRemainingAmount(plan as StudentPaymentPlan),
        0,
      ),
    [selectedPlans],
  );

  const selectedType = watch("type");
  const watchedAmount = watch("amount") || 0;

  useEffect(() => {
    if (selectedType === DiscountType.BOURSE) {
      setDiscountMode("PERCENTAGE");
      setPercentage(100);
    }
  }, [selectedType]);

  useEffect(() => {
    if (discountMode === "PERCENTAGE") {
      if (
        selectedType !== DiscountType.BOURSE &&
        percentage === 0 &&
        watchedAmount > 0 &&
        selectedPlansRemaining > 0
      ) {
        setPercentage(
          Math.min(
            100,
            Math.round((watchedAmount / selectedPlansRemaining) * 100),
          ),
        );
        return;
      }

      const amountValue = Math.round(
        (selectedPlansRemaining * percentage) / 100,
      );
      setValue("amount", amountValue, { shouldValidate: true });
      return;
    }

    const percentValue = selectedPlansRemaining
      ? Math.round((watchedAmount / selectedPlansRemaining) * 100)
      : 0;
    setPercentage(percentValue);
  }, [
    discountMode,
    percentage,
    selectedPlansRemaining,
    setValue,
    watchedAmount,
    selectedType,
  ]);

  useEffect(() => {
    if (!show) return;
    setValue("ids_paiement_plan", []);
    setPlanAllocations({});
    setPaiementPlans([]);
    ++plansRequest.current;
    if (selectedStudent?.id) {
      fetchStudentPlanForStudentSelected();
      setLoadingParents(true);
      getStudentById(
        selectedStudent.id,
        buildPermissionContext(Resource.BOURSE_REMISE, Action.CREATE),
      )
        .then((response) => setStudentParents((response.data?.parents || []).filter((parent: any) => parent.statut !== false && !parent.isDeceased)))
        .catch(() => setStudentParents([]))
        .finally(() => setLoadingParents(false));
    } else {
      setStudentParents([]);
    }
    setValue("correspondant", "");
    setValue("number_corresponding", "");
    return () => { ++plansRequest.current; };
  }, [show, selectedStudent?.id, selectedYear?.id]);

  useEffect(() => {
    setPlanAllocations((prev) => {
      const next: Record<string, number> = {};
      id_paymentPlans.forEach((pid: string) => {
        next[pid] = prev[pid] ?? 0;
      });
      return next;
    });
  }, [id_paymentPlans]);

  const totalAllocated = useMemo(
    () => Object.values(planAllocations).reduce((s, v) => s + (v || 0), 0),
    [planAllocations],
  );

  return (
    <>
      <Modal show={show} onHide={() => onClose()} size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Nouvelle Bourse / Remise</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white" id="create_bourse_remise_modal">
          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => clearErrors("root")}
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row g-3">
              <div className="col-12">
                <div className="card shadow-sm border-dark rounded">
                  <div className="card-header bg-light d-flex align-items-center justify-content-between">
                    <h6 className="mb-0">Informations</h6>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label">Étudiant :</label>
                        <br />
                        {lockStudent ? (
                          <>
                            <span>
                              {selectedStudent?.levelAbbr
                                ? `${selectedStudent?.levelAbbr} - ${selectedStudent?.classAbbr} `
                                : ""}
                              {selectedStudent?.lastname}{" "}
                              {selectedStudent?.firstname}
                            </span>
                          </>
                        ) : (
                          <Select
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(input.toString().toLowerCase())
                            }
                            size="large"
                            style={{ width: "100%" }}
                            getPopupContainer={getModalContainer}
                            placeholder="Sélectionner un étudiant"
                            disabled={lockStudent}
                            value={watch("id_studentProfil") || undefined}
                            onChange={(value) =>
                              setValue("id_studentProfil", value as string, {
                                shouldValidate: true,
                              })
                            }
                            status={
                              errors.id_studentProfil ? "error" : undefined
                            }
                            options={students.map((s) => ({
                              label: `${s.levelAbbr ? `${s.levelAbbr} - ${s.classAbbr} ` : ""}${s.lastname} ${s.firstname}`,
                              value: s.id,
                            }))}
                            className={
                              errors.id_studentProfil
                                ? "ant-select-status-error"
                                : undefined
                            }
                          />
                        )}
                        {errors.id_studentProfil && (
                          <div className="invalid-feedback">
                            {errors.id_studentProfil.message}
                          </div>
                        )}
                      </div>

                      <div className="col-12">
                        <label className="form-label">Sélectionner un responsable (optionnel)</label>
                        <Select
                          allowClear
                          showSearch
                          loading={loadingParents}
                          disabled={!selectedStudent?.id || loadingParents || studentParents.length === 0}
                          style={{ width: "100%" }}
                          placeholder={studentParents.length ? "Choisir un parent ou responsable" : "Aucun responsable actif associé"}
                          getPopupContainer={getModalContainer}
                          options={studentParents.map((parent: any) => ({
                            value: parent.id,
                            label: `${parent.link?.type ? `${parent.link.type} — ` : ""}${parent.firstname || ""} ${parent.lastname || ""}${parent.number ? ` — ${parent.number}` : ""}`,
                          }))}
                          filterOption={(input, option) => (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())}
                          onChange={(parentId) => {
                            const parent = studentParents.find((item: any) => item.id === parentId);
                            setValue("correspondant", parent ? `${parent.firstname || ""} ${parent.lastname || ""}`.trim() : "");
                            setValue("number_corresponding", parent?.number || "");
                          }}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          Correspondant (optionnel)
                        </label>
                        <input
                          {...register("correspondant")}
                          type="text"
                          className="form-control"
                          placeholder="Ex: Parent, tuteur, sponsor..."
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">
                          Numéro Correspondant (optionnel)
                        </label>
                        <input
                          {...register("number_corresponding")}
                          type="text"
                          className="form-control"
                          placeholder="Ex: +223 602037404"
                        />
                      </div>
                      <div className="d-flex flex-column  justify-content-between col-12 col-md-6">

                        <div className="col-12 col-md-4">
                          <label className="form-label">Type</label>
                          <div className="form-switch">
                            <input
                              // {...register("type")}
                              className={
                                errors.type
                                  ? "form-check-input is-invalid"
                                  : "form-check-input"
                              }
                              type="checkbox"
                              role="switch"
                              id="typeSwitch"
                              onChange={() => {
                                setValue(
                                  "type",
                                  watch("type") === DiscountType.REMISE
                                    ? DiscountType.BOURSE
                                    : DiscountType.REMISE,
                                );
                              }}
                              checked={watch("type") === DiscountType.REMISE}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="typeSwitch"
                            >
                              {watch("type") === DiscountType.REMISE
                                ? " Remise"
                                : " Bourse"}
                            </label>
                          </div>
                          {errors.type && (
                            <div className="invalid-feedback">
                              {errors.type.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-12">
                        <label className="form-label">Description</label>
                        <textarea
                          {...register("description")}
                          className={
                            errors.description
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          rows={3}
                          placeholder="Ex: Bourse attribuée aux étudiants méritants"
                        />
                        {errors.description && (
                          <div className="invalid-feedback">
                            {errors.description.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="card shadow-sm border-dark rounded">
                  <div className="card-header bg-light d-flex align-items-center justify-content-between">
                    <h6 className="mb-0">Plans de paiement</h6>
                     {/* Dropdown des plans */}
                      <div className="dropdown">
                        <button
                          className="btn btn-outline-light bg-white dropdown-toggle"
                          type="button"
                          disabled={loadingPlans || !selectedStudent?.id}
                          data-bs-toggle="dropdown"
                          style={{ minWidth: "220px" }}
                        >
                          <i className="ti ti-wallet me-2" />
                          {id_paymentPlans?.length > 0
                            ? `${id_paymentPlans.length} tranche(s)`
                            : "Plans de paiement"}
                        </button>

                        <ul
                          className="dropdown-menu p-2"
                          onClick={(e) => e.stopPropagation()}
                          style={{ minWidth: "240px" }}
                        >
                          {paiementPlans.map((plan) => (
                            <li key={plan.id} className="mb-2">
                              <label
                                className={`dropdown-item d-flex flex-column ${
                                  id_paymentPlans?.includes(plan.id)
                                    ? "bg-light text-primary rounded-1"
                                    : ""
                                }`}
                                style={{ cursor: "pointer" }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <input
                                    disabled={loadingPlans || getRemainingAmount(plan) <= 0}
                                    type="checkbox"
                                    className="form-check-input m-0"
                                    checked={id_paymentPlans?.includes(plan.id)}
                                    onChange={() => {
                                      if (id_paymentPlans?.includes(plan.id)) {
                                        setValue(
                                          "ids_paiement_plan",
                                          id_paymentPlans?.filter(
                                            (id) => id !== plan.id,
                                          ),
                                        );
                                      } else {
                                        setValue("ids_paiement_plan", [
                                          ...id_paymentPlans,
                                          plan.id,
                                        ]);
                                      }
                                    }}
                                  />
                                  <span>{plan?.PaymentPlan?.label}</span>
                                </div>
                                <div className="ms-4">
                                  <small className="text-muted">
                                    {getRemainingAmount(plan) <= 0 ? "Soldé" : `Reste à payer : ${getRemainingAmount(plan).toLocaleString("fr-FR")} ${currency}`}
                                  </small>
                                </div>
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    <button
                      type="button"
                      className="btn btn-info btn-sm"
                      onClick={handleAutoDistribute}
                      disabled={!id_paymentPlans?.length || totalAmount <= 0}
                    >
                      Auto-répartition
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="d-flex align-items-start gap-3">
                     

                      {/* Aperçu des plans sélectionnés */}

                      {loadingPlans ? (
                        <span className="text-center w-100 h-100 fs-16">
                          Chargement des plans...
                        </span>
                      ) : (
                        <div className="flex-grow-1">
                          {id_paymentPlans?.length > 0 ? (
                            <>
                              <div className="d-flex flex-column gap-2">
                                {paiementPlans
                                  .filter((p) => id_paymentPlans.includes(p.id))
                                  .map((plan) => {
                                    const maxAmount = getRemainingAmount(plan);
                                    const pid = plan.id;
                                    const val = planAllocations[pid] ?? 0;
                                    return (
                                      <div
                                        key={pid}
                                        className="d-flex align-items-center justify-content-between border rounded-2 p-2"
                                      >
                                        <div className="d-flex flex-column">
                                          <span className="fw-semibold">
                                            {plan?.PaymentPlan?.label}
                                          </span>
                                          <small className="text-muted">
                                            {maxAmount?.toLocaleString("fr-FR")}{" "}
                                            {currency}
                                          </small>
                                        </div>
                                        <div
                                          className="input-group"
                                          style={{ width: 220 }}
                                        >
                                          <input
                                            type="number"
                                            className="form-control"
                                            min={0}
                                            max={maxAmount}
                                            value={val}
                                            onChange={(e) => {
                                              const num = parseFloat(
                                                e.target.value || "0",
                                              );
                                              setPlanAllocations((prev) => ({
                                                ...prev,
                                                [pid]: Math.max(
                                                  0,
                                                  isNaN(num) ? 0 : num,
                                                ),
                                              }));
                                            }}
                                          />
                                          <span className="input-group-text">
                                            {currency}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                                                      <div>
                          <label className="form-label">
                            Montant / Pourcentage
                          </label>
                          <div className="d-flex align-items-center gap-2 mb-2">
                           
                            {discountMode === "AMOUNT" ? (
                              <div className="input-group mb-0" style={{ flex: 1 }}>
                                <input
                                  {...register("amount", { valueAsNumber: true })}
                                  type="number"
                                  min={0}
                                  className={
                                    errors.amount
                                      ? "form-control is-invalid"
                                      : "form-control"
                                  }
                                  placeholder="Ex: 150 000 FCFA"
                                />
                                <span className="input-group-text">{currency}</span>
                              </div>
                            ) : (
                              <div className="input-group mb-0" style={{ flex: 1 }}>
                                <input
                                  type="number"
                                  className="form-control"
                                  min={0}
                                  max={100}
                                  value={percentage}
                                  onChange={(e) => {
                                    const value = Math.max(
                                      0,
                                      Math.min(100, parseFloat(e.target.value || "0")),
                                    );
                                    setPercentage(value);
                                  }}
                                />
                                <span className="input-group-text">%</span>
                              </div>
                            )}
                             <div className="form-switch mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="discountModeSwitch"
                                checked={discountMode === "PERCENTAGE"}
                                onChange={() =>
                                  setDiscountMode(
                                    discountMode === "AMOUNT"
                                      ? "PERCENTAGE"
                                      : "AMOUNT",
                                  )
                                }
                              />
                              <label
                                className="form-check-label"
                                htmlFor="discountModeSwitch"
                              >
                                {discountMode === "PERCENTAGE"
                                  ? "Pourcentage"
                                  : "Montant"}
                              </label>
                            </div>
                          </div>
                          <div className="form-text mb-2">
                            {discountMode === "AMOUNT" ? (
                              selectedPlansRemaining > 0 ? (
                                <>Ce montant correspond à {Math.round((watchedAmount / selectedPlansRemaining) * 100)}% du total sélectionné</>
                              ) : (
                                <>Sélectionnez des plans pour calculer le pourcentage</>
                              )
                            ) : selectedPlansRemaining > 0 ? (
                              <>Ce pourcentage correspond à {Math.round((selectedPlansRemaining * percentage) / 100).toLocaleString("fr-FR")} {currency}</>
                            ) : (
                              <>Sélectionnez des plans pour calculer le montant</>
                            )}
                          </div>
                          {errors.amount && (
                            <div className="invalid-feedback">
                              {errors.amount.message}
                            </div>
                          )}
                        </div>
                              <div className="d-flex align-items-center justify-content-end gap-3 mt-2">
                                <span className="badge bg-warning-subtle text-dark">
                                  Montant total (plans):{" "}
                                 {selectedPlansRemaining.toLocaleString("fr-FR")} {currency}
                                </span>
                                <span className="badge bg-primary-subtle text-dark">
                                  Total couvert:{" "}
                                  {totalAllocated.toLocaleString("fr-FR")}{" "}
                                  {currency}
                                </span>
                                <span
                                  className={`badge ${
                                    totalAllocated > totalAmount
                                      ? "bg-danger-subtle text-dark"
                                      : "bg-success-subtle text-dark"
                                  }`}
                                >
                                  Reste (Bourse):{" "}
                                  {Math.max(
                                    0,
                                    totalAmount - totalAllocated,
                                  ).toLocaleString("fr-FR")}{" "}
                                  {currency}
                                </span>
                              </div>
                            </>
                          ) : (
                            <span className="text-muted fs-3">
                              Aucun plan sélectionné
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {errors.ids_paiement_plan && (
                      <div className="invalid-feedback d-block">
                        {errors.ids_paiement_plan.message}
                      </div>
                    )}
                    {totalAllocated > totalAmount && (
                      <div className="text-danger small mt-2">
                        La somme des montants par plan dépasse le montant de la
                        bourse/remise.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-danger me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting || loadingPlans}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateBourseRemiseModal,
  [buildPermission(Resource.BOURSE_REMISE, Action.CREATE)],
  "masquer",
);
